import { useQuery } from "@tanstack/react-query";
import { usePublicClient } from "wagmi";

import { LOAN_ADDRESSES, LOAN_ROUTER_V2_ABI } from "./contracts";
import { Loan } from "./data/types";
import { isQuoteQuarantined } from "./quarantinedQuotes";

export function useRepayInfo(loan: Loan, isRepayment: boolean) {
  const { chainId } = loan;
  const pClient = usePublicClient({ chainId });

  const { nextDeadline } = loan;

  const isQuarantined = isQuoteQuarantined(loan);

  // `loan.currentQuote` is the server-rendered `quote(terms)` snapshot from
  // getLoanTermsOnchainData — the quote as of now, which is exactly what the
  // `isRepayment` branch below returns. Seed it so the amounts paint on first
  // render rather than as skeletons; staleTime is 0, so this still refetches on
  // mount. Skipped when quoting at the next deadline (different amounts) or when
  // quarantined (the snapshot comes from the same broken call). `gracePeriodPayment`
  // needs the deadline quote to derive, so it seeds at 0 and fills in on refetch.
  const initialData =
    isRepayment && !isQuarantined && loan.currentQuote
      ? { ...loan.currentQuote, gracePeriodPayment: 0n }
      : undefined;

  return useQuery({
    initialData,
    queryKey: ["quote", loan.loanTermsHash, isRepayment] as const,
    queryFn: async ({ queryKey }) => {
      const [, , isRepayment] = queryKey;

      if (!pClient) throw new Error("No public client");

      const loanRouter = LOAN_ADDRESSES[chainId].loanRouterV2;

      const [quoteNow, quoteAtDeadline] = await pClient.multicall({
        contracts: [
          /* quote now */
          {
            address: loanRouter,
            abi: LOAN_ROUTER_V2_ABI,
            functionName: "quote",
            args: [loan.termsStruct, BigInt(Math.floor(Date.now() / 1000))],
          },
          /* quote at repayment deadline */
          {
            address: loanRouter,
            abi: LOAN_ROUTER_V2_ABI,
            functionName: "quote",
            args: [loan.termsStruct, BigInt(nextDeadline)],
          },
        ],
        allowFailure: false,
      });

      if (isRepayment) {
        return {
          principal: quoteNow[0],
          interest: quoteNow[1],
          fees: quoteNow[2],
          total: quoteNow[0] + quoteNow[1] + quoteNow[2],
          gracePeriodPayment: quoteNow[1] - quoteAtDeadline[1],
        };
      }

      return {
        principal: quoteAtDeadline[0],
        interest: quoteAtDeadline[1],
        fees: quoteAtDeadline[2],
        total: quoteAtDeadline[0] + quoteAtDeadline[1] + quoteAtDeadline[2],
        gracePeriodPayment: 0n,
      };
    },
    enabled: Boolean(pClient) && nextDeadline != undefined && !isQuarantined,
  });
}
